/**
 * Expand a user list against a context (e.g. schema users) to figure out
 * which list members are actually applicable here.
 *
 * Pure function — no I/O. Used at apply time inside <UserListPicker>.
 *
 * @param {object}   args
 * @param {{ id: string, name: string, members: string[] }} args.list
 * @param {Array<{ id: string }>} args.contextUsers - users available in the host context
 * @param {Set<string>} args.excludeIds - already-shared, current-user, owner, etc.
 * @returns {{
 *   expanded: Array<{ user: object, source: { listId: string, listName: string } }>,
 *   skippedOutOfContext: number,
 *   skippedAlreadyShared: number,
 * }}
 */
export function expandList({ list, contextUsers, excludeIds }) {
  const lowerExclude = new Set(
    Array.from(excludeIds || []).map((id) => String(id).toLowerCase()),
  );
  const byIdLower = new Map();
  for (const u of contextUsers || []) {
    if (u && u.id != null) byIdLower.set(String(u.id).toLowerCase(), u);
  }

  const expanded = [];
  let skippedOutOfContext = 0;
  let skippedAlreadyShared = 0;

  for (const memberId of list.members || []) {
    const key = String(memberId).toLowerCase();
    const user = byIdLower.get(key);
    if (!user) {
      skippedOutOfContext += 1;
      continue;
    }
    if (lowerExclude.has(key)) {
      skippedAlreadyShared += 1;
      continue;
    }
    expanded.push({
      user,
      source: { listId: list.id, listName: list.name },
    });
  }

  return { expanded, skippedOutOfContext, skippedAlreadyShared };
}
